import { ref, onBeforeUnmount } from 'vue';

/**
 * Drives a single user-resizable grid column for the matter-context tables.
 * The resizable column's width is exposed as a ref (apply it as a CSS var on
 * the table); the flexible `1fr` neighbor column absorbs the change, so the
 * drag handle adjusts the split between the two without leaving gaps.
 */
export interface ResizableColumnOptions {
    initialWidth?: number;
    minWidth?: number;
    /** Minimum width to preserve for the flexible (1fr) neighbor column. */
    neighborMinWidth?: number;
    /** Combined width of the other fixed columns (everything except this
     *  column and the flexible neighbor). */
    fixedColumnsWidth: number;
}

export function useResizableColumn(options: ResizableColumnOptions) {
    const {
        initialWidth = 220,
        minWidth = 120,
        neighborMinWidth = 200,
        fixedColumnsWidth,
    } = options;

    const tableEl = ref<HTMLElement | null>(null);
    const width = ref(initialWidth);

    let startX = 0;
    let startWidth = 0;
    let maxWidth = initialWidth;

    const onMove = (event: MouseEvent) => {
        const next = startWidth + (event.clientX - startX);
        width.value = Math.min(maxWidth, Math.max(minWidth, next));
    };

    const endResize = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', endResize);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    };

    const startResize = (event: MouseEvent) => {
        event.preventDefault();
        startX = event.clientX;
        startWidth = width.value;
        // Cap so the neighbor keeps at least neighborMinWidth at the current
        // table width.
        const tableW = tableEl.value?.clientWidth ?? 0;
        maxWidth = Math.max(minWidth, tableW - fixedColumnsWidth - neighborMinWidth);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', endResize);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    };

    onBeforeUnmount(endResize);

    return { tableEl, width, startResize };
}
